import { DirectionEnum, TRestrictedDirection } from "../enums";
import {
  IHubSettingsDirectionsPair,
  IStationHubSettingsEntry,
  THubSettingsDirection,
  TStationHubMap,
  TStationHubMapValue,
} from "../models";
import { isValidPositiveNumber } from "./numbers.helpers";

const toPositiveNumber = (value: unknown): number | undefined => {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue) || numberValue <= 0) {
    return undefined;
  }
  return numberValue;
};

export const isHubSettingsDirection = (
  value: unknown,
): value is THubSettingsDirection => {
  return (
    value === DirectionEnum.PickUp ||
    value === DirectionEnum.DropOff ||
    value === DirectionEnum.Both
  );
};

export const expandHubSettingsDirections = (
  direction: THubSettingsDirection,
): TRestrictedDirection[] => {
  if (direction === DirectionEnum.Both) {
    return [DirectionEnum.PickUp, DirectionEnum.DropOff];
  }
  return [direction];
};

export const hubSettingsDirectionsOverlap = ({
  left,
  right,
}: IHubSettingsDirectionsPair): boolean => {
  const leftSet = new Set(expandHubSettingsDirections(left));
  return expandHubSettingsDirections(right).some(direction =>
    leftSet.has(direction),
  );
};

export const mergeHubSettingsDirections = ({
  left,
  right,
}: IHubSettingsDirectionsPair): THubSettingsDirection => {
  if (left === right) {
    return left;
  }
  return DirectionEnum.Both;
};

export const formatHubSettingsDirectionLabel = (
  direction?: THubSettingsDirection,
): string => {
  if (direction === DirectionEnum.PickUp) {
    return "A";
  }
  if (direction === DirectionEnum.DropOff) {
    return "B";
  }
  return "A/B";
};

const parseDirectionObjectEntries = ({
  pairedStationId,
  value,
}: {
  pairedStationId: number;
  value: Record<string, unknown>;
}): IStationHubSettingsEntry[] => {
  const bothHubId = toPositiveNumber(value[DirectionEnum.Both]);
  if (bothHubId) {
    return [
      { pairedStationId, hubId: bothHubId, direction: DirectionEnum.Both },
    ];
  }

  const entries: IStationHubSettingsEntry[] = [];
  const pickUpHubId = toPositiveNumber(value[DirectionEnum.PickUp]);
  if (pickUpHubId) {
    entries.push({
      pairedStationId,
      hubId: pickUpHubId,
      direction: DirectionEnum.PickUp,
    });
  }

  const dropOffHubId = toPositiveNumber(value[DirectionEnum.DropOff]);
  if (dropOffHubId) {
    entries.push({
      pairedStationId,
      hubId: dropOffHubId,
      direction: DirectionEnum.DropOff,
    });
  }

  return entries;
};

export const parseHubSettingsEntries = (
  hubSettings?: TStationHubMap | null,
): IStationHubSettingsEntry[] => {
  if (!hubSettings) {
    return [];
  }

  const entries: IStationHubSettingsEntry[] = [];
  Object.entries(hubSettings).forEach(([pairedStationIdRaw, rawValue]) => {
    const pairedStationId = toPositiveNumber(pairedStationIdRaw);
    if (!pairedStationId) {
      return;
    }

    if (
      rawValue !== null &&
      typeof rawValue === "object" &&
      !Array.isArray(rawValue)
    ) {
      entries.push(
        ...parseDirectionObjectEntries({
          pairedStationId,
          value: rawValue as Record<string, unknown>,
        }),
      );
      return;
    }

    const hubId = toPositiveNumber(rawValue);
    if (hubId) {
      entries.push({
        pairedStationId,
        hubId,
        direction: DirectionEnum.Both,
      });
    }
  });

  return entries;
};

export const hubSettingsEntriesToMap = (
  entries: IStationHubSettingsEntry[],
): TStationHubMap => {
  const grouped = new Map<number, IStationHubSettingsEntry[]>();
  entries.forEach(entry => {
    const list = grouped.get(entry.pairedStationId) ?? [];
    list.push(entry);
    grouped.set(entry.pairedStationId, list);
  });

  const result: TStationHubMap = {};
  grouped.forEach((list, pairedStationId) => {
    const bothEntry = list.find(
      entry =>
        entry.direction === DirectionEnum.Both &&
        isValidPositiveNumber(entry.hubId),
    );
    if (bothEntry) {
      result[pairedStationId] = bothEntry.hubId;
      return;
    }

    const pickUpEntry = list.find(
      entry => entry.direction === DirectionEnum.PickUp,
    );
    const dropOffEntry = list.find(
      entry => entry.direction === DirectionEnum.DropOff,
    );
    if (pickUpEntry && dropOffEntry) {
      const directionValue: TStationHubMapValue = {
        [DirectionEnum.PickUp]: pickUpEntry.hubId,
        [DirectionEnum.DropOff]: dropOffEntry.hubId,
      };
      result[pairedStationId] = directionValue;
      return;
    }

    if (pickUpEntry) {
      result[pairedStationId] = { [DirectionEnum.PickUp]: pickUpEntry.hubId };
      return;
    }

    if (dropOffEntry) {
      result[pairedStationId] = { [DirectionEnum.DropOff]: dropOffEntry.hubId };
    }
  });

  return result;
};
